import * as React from "react";
import { FormEvent, useState } from "react";
import { useResetPasswordMutation } from "../../../redux/services/auth";
import AuthActionButton from "../components/auth-action-button";
import AuthFormInput from "../components/auth-form-input";
import AuthFormTitle from "../components/auth-form-title";
import AuthLink from "../components/auth-link";
import AuthSuccessMessage from "../components/auth-success-message";

export type ResetPasswordState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "reset_complete" }
  | {
      status: "error";
      state: "token_invalid" | "unexpected_error";
      message: string;
    };

const ResetPassword: React.FC<{ token: string }> = ({ token }) => {
  const [requestState, setRequestState] = useState<ResetPasswordState>({
    status: "idle",
  });
  const [resetPassword] = useResetPasswordMutation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (requestState.status === "submitting") return;

    setRequestState({ status: "submitting" });

    const formData = new FormData(e.currentTarget);
    const password = formData.get("password") as string;

    try {
      await resetPassword({
        password,
        token,
      }).unwrap();

      setRequestState({ status: "reset_complete" });
    } catch (error: any) {
      if (
        error?.data?.code === "TOKEN_INVALID" ||
        error?.data?.code === "TOKEN_ALREADY_USED"
      ) {
        setRequestState({
          status: "error",
          state: "token_invalid",
          message:
            "The password reset link is invalid or has expired. Please request a new one.",
        });
        return;
      }

      setRequestState({
        status: "error",
        state: "unexpected_error",
        message:
          error?.data?.message ||
          "An unexpected error occurred. Please try again.",
      });
    }
  };

  if (requestState.status === "reset_complete") {
    return (
      <div className="mb-3">
        <AuthSuccessMessage
          title="Password Reset Complete!"
          message="Your password has been successfully reset."
          helpText="You can now log in with your new password."
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-column gap-4">
      <AuthFormTitle label="Reset Your Password" />

      <AuthFormInput
        id="password"
        name="password"
        label="New Password"
        type="password"
        placeholder="Create a password"
        required
        minLength={8}
        helpText="Password must be at least 8 characters long"
        error={requestState.status === "error" ? requestState.message : ""}
      />

      <AuthActionButton
        label="Reset Password"
        disabled={requestState.status === "submitting"}
        loading={requestState.status === "submitting"}
      />

      <AuthLink linkText="Back to Login" to="/login" className="text-center" />
    </form>
  );
};

export default ResetPassword;
